import { jsPDF } from "jspdf";

import {
	CostRateConfig,
	ReportData,
	ReportFormat,
	UsageRecord,
	UserUsageBreakdown,
} from "../types/usageCostAllocation";

export function generateCsvReport(data: ReportData, costingEnabled: boolean = true): string {
	const lines: string[] = [];

	// Summary section
	lines.push("Summary");
	lines.push(costingEnabled
		? "Billing Period,Total Billable Units,Total Estimated Cost"
		: "Billing Period,Total Billable Units");
	lines.push(
		(costingEnabled
			? [data.billingPeriod, data.summary.totalCharacterCount, data.summary.totalEstimatedCost]
			: [data.billingPeriod, data.summary.totalCharacterCount]
		).join(",")
	);

	// Blank line separator
	lines.push("");

	// Per-user breakdown section
	lines.push("Per-User Breakdown");
	lines.push(costingEnabled
		? "User ID,Total Billable Units,Total Estimated Cost"
		: "User ID,Total Billable Units");
	data.userBreakdown.forEach((user: UserUsageBreakdown) => {
		lines.push(
			(costingEnabled
				? [user.userName || user.userId, user.totalCharacterCount, user.totalEstimatedCost]
				: [user.userName || user.userId, user.totalCharacterCount]
			).join(",")
		);
	});

	// Blank line separator
	lines.push("");

	// Detail records section
	lines.push("Detail Records");
	lines.push(costingEnabled
		? "ID,User ID,Timestamp,Billing Period,KB,Cost Rate Applied,Estimated Cost,Incomplete"
		: "ID,User ID,Timestamp,Billing Period,KB,Incomplete"
	);
	data.records.forEach((record: UsageRecord) => {
		lines.push(
			(costingEnabled
				? [
					record.id, record.userName || record.userId,
					record.timestamp, record.billingPeriod,
					record.metricValue, record.costRateApplied, record.estimatedCost,
					record.incomplete ?? false,
				]
				: [
					record.id, record.userName || record.userId,
					record.timestamp, record.billingPeriod,
					record.metricValue, record.incomplete ?? false,
				]
			).join(",")
		);
	});

	// Cost rates section (omitted when costing disabled)
	if (costingEnabled) {
		// Blank line separator
		lines.push("");

		lines.push("Cost Rates");
		lines.push("Character Cost Rate,Effective Timestamp");
		data.costRates.forEach((rate: CostRateConfig) => {
			lines.push(
				[
					rate.characterCostRate,
					rate.effectiveTimestamp,
				].join(",")
			);
		});
	}

	// AWS Account Cost section (conditional)
	if (data.awsAccountCost) {
		lines.push("");
		lines.push("AWS Account Cost");
		lines.push("Billing Period,Cost Amount,Currency");
		lines.push(
			[
				data.awsAccountCost.billingPeriod,
				data.awsAccountCost.costAmount.toFixed(2),
				data.awsAccountCost.currency,
			].join(",")
		);
	}

	return lines.join("\n");
}

export function generateJsonReport(data: ReportData, costingEnabled: boolean = true): string {
	if (costingEnabled) {
		const filteredRecords = data.records.map(
			({ jobType: _jt, metricType: _mt, jobId: _ji, userName, userId, ...rest }) => ({
				...rest,
				userId: userName || userId,
			})
		);
		const filteredUserBreakdown = data.userBreakdown.map(
			({ userName, userId, ...rest }) => ({
				...rest,
				userId: userName || userId,
			})
		);
		return JSON.stringify({ ...data, records: filteredRecords, userBreakdown: filteredUserBreakdown }, null, 2);
	}

	const { totalEstimatedCost: _sc, ...summaryRest } = data.summary;

	const filteredUserBreakdown = data.userBreakdown.map(
		({ totalEstimatedCost: _uc, userName, userId, ...rest }) => ({
			...rest,
			userId: userName || userId,
		})
	);

	const filteredRecords = data.records.map(
		({ costRateApplied: _cr, estimatedCost: _ec, jobType: _jt, metricType: _mt, jobId: _ji, userName, userId, ...rest }) => ({
			...rest,
			userId: userName || userId,
		})
	);

	const { costRates: _rates, ...dataRest } = data;

	const filtered = {
		...dataRest,
		summary: summaryRest,
		userBreakdown: filteredUserBreakdown,
		records: filteredRecords,
	};

	return JSON.stringify(filtered, null, 2);
}

export function generatePdfReport(data: ReportData, costingEnabled: boolean = true): Blob {
	const doc = new jsPDF();
	let y = 20;
	const lineHeight = 7;
	const pageHeight = 280;

	const addLine = (text: string, fontSize = 10) => {
		if (y > pageHeight) {
			doc.addPage();
			y = 20;
		}
		doc.setFontSize(fontSize);
		doc.text(text, 14, y);
		y += lineHeight;
	};

	// Summary section
	addLine("Usage Report", 16);
	y += 4;
	addLine("Summary", 12);
	addLine(`Billing Period: ${data.billingPeriod}`);
	addLine(`Total Billable Units: ${data.summary.totalCharacterCount}`);
	if (costingEnabled) {
		addLine(`Total Estimated Cost: ${data.summary.totalEstimatedCost}`);
	}
	y += 4;

	// Per-user breakdown section
	addLine("Per-User Breakdown", 12);
	data.userBreakdown.forEach((user: UserUsageBreakdown) => {
		addLine(
			costingEnabled
				? `${user.userName || user.userId}: BillableUnits=${user.totalCharacterCount}, Cost=${user.totalEstimatedCost}`
				: `${user.userName || user.userId}: BillableUnits=${user.totalCharacterCount}`
		);
	});
	y += 4;

	// Detail records section
	addLine("Detail Records", 12);
	data.records.forEach((record: UsageRecord) => {
		addLine(
			costingEnabled
				? `KB: ${record.metricValue} | Rate: ${record.costRateApplied} | Cost: ${record.estimatedCost}`
				: `KB: ${record.metricValue}`
		);
	});
	y += 4;

	// Cost rates section (omitted when costing disabled)
	if (costingEnabled) {
		addLine("Cost Rates", 12);
		data.costRates.forEach((rate: CostRateConfig) => {
			addLine(
				`Char Rate: ${rate.characterCostRate}, Effective: ${rate.effectiveTimestamp}`
			);
		});
	}

	// AWS Account Cost section (conditional)
	if (data.awsAccountCost) {
		y += 4;
		addLine("AWS Account Cost", 12);
		addLine(
			`Billing Period: ${data.awsAccountCost.billingPeriod}, Cost: ${data.awsAccountCost.costAmount.toFixed(2)} ${data.awsAccountCost.currency}`
		);
	}

	return doc.output("blob");
}

export function exportReport(data: ReportData, format: ReportFormat, costingEnabled: boolean = true): Blob {
	switch (format) {
		case "csv": {
			const csv = generateCsvReport(data, costingEnabled);
			return new Blob([csv], { type: "text/csv" });
		}
		case "json": {
			const json = generateJsonReport(data, costingEnabled);
			return new Blob([json], { type: "application/json" });
		}
		case "pdf":
			return generatePdfReport(data, costingEnabled);
		default:
			throw new Error(`Unsupported report format: ${format}`);
	}
}
